// PHI-scrubbing hooks for Sentry (beforeSend / beforeBreadcrumb).
// Regex-detectable PII only — free-form names cannot be reliably caught client-side (documented limitation).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Existing Sign generators: integer id, Date.now() milliseconds, random hex.
const GENERATED_SIGN_CREDENTIAL = /^[+-]?\d+-\d{13}-[0-9a-f]+$/i;
const PHI_KEY_HINTS = ["email", "name", "dob", "phone", "ssn", "patient", "token", "address", "mrn", "cookie", "signature", "field_values", "pdf_base64", "sign-secret", "signsecret", "sign_secret"];

const EMAIL = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const ISO_DATE = /\b\d{4}-\d{2}-\d{2}\b/g;
const SLASH_DATE = /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b|\b\d{4}\/\d{2}\/\d{2}\b/g;
const PHONE = /\(?\b\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/g;
const LONGNUM = /\d{6,}/g; // MRNs, long numeric ids (no word boundary — catches ids embedded after letters)
const credentialRoutes = new Set(["cp-document-sign", "operator-sign", "collab-phys-sign", "collab-operator-sign", "sign", "collab-charts"]);
// publicRoutes.js also has /:signSecretId; only its registered static paths are safe.
const staticRootRoutes = new Set(["login", "forgot-password", "reset-password", "signed-documents",
  "cp-document-drafts", "prepare-template", "copy-template", "send-template", "send", "thank-you",
  "collab-phys-sign-all", "collab-operator-sign-all", "cp-documents-sign-all", "electronic_record_and_signature_disclosure"]);
const routeSegment = (segment) => {
  try { return decodeURIComponent(segment || "").toLowerCase(); }
  catch { return ""; }
};

function credentialIndexes(path, segments) {
  // Remove only the authority, not dot segments: navigation breadcrumbs can
  // contain a raw credential that URL.pathname would normalize away.
  const authority = path.match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/]*/i)?.[0] || "";
  const parts = path.slice(authority.length).split("/");
  const offset = segments.length - parts.length;
  const indexes = new Set();
  parts.forEach((part, index) => {
    const route = routeSegment(parts[index - 1]);
    if (credentialRoutes.has(route)
      && !(route === "collab-phys-sign" && routeSegment(part) === "complete")) indexes.add(offset + index);
  });
  const first = parts.findIndex(Boolean);
  if (first >= 0 && parts.filter(Boolean).length === 1 && !staticRootRoutes.has(routeSegment(parts[first]))) {
    indexes.add(offset + first);
  }
  return indexes;
}

export function redactPii(text) {
  return text
    .replace(EMAIL, "[email]")
    .replace(SSN, "[ssn]")
    .replace(ISO_DATE, "[date]")
    .replace(SLASH_DATE, "[date]")
    .replace(PHONE, "[phone]")
    .replace(LONGNUM, "[id]");
}

// Drop the query string + fragment, replace whole id-like segments with ":id",
// and redact PII embedded inside any remaining segment.
export function scrubUrl(url) {
  if (!url) return url;
  const hadQuery = url.includes("?");
  const path = url.split("?")[0].split("#")[0];
  const segments = path.split("/");
  const secretIndexes = credentialIndexes(path, segments);
  const redacted = segments
    .map((seg, index) =>
      secretIndexes.has(index) || GENERATED_SIGN_CREDENTIAL.test(routeSegment(seg))
        || /^\d{4,}$/.test(seg) || UUID.test(seg) || seg.includes("@") || /^[0-9a-f]{16,}$/i.test(seg)
        ? ":id"
        : redactPii(seg),
    )
    .join("/");
  return hadQuery ? `${redacted}?[filtered]` : redacted;
}

// Recurse through a structured bag: drop PHI-keyed entries, redact string values.
// A WeakSet breaks reference cycles so a circular object can't throw and drop the event.
function deepScrub(value, seen = new WeakSet()) {
  if (typeof value === "string") return redactPii(value);
  if (value && typeof value === "object") {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    if (Array.isArray(value)) return value.map((v) => deepScrub(v, seen));
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (PHI_KEY_HINTS.some((h) => k.toLowerCase().includes(h))) continue; // drop PHI-keyed
      out[k] = deepScrub(v, seen);
    }
    return out;
  }
  return value;
}

export function scrubEvent(event) {
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.data) event.request.data = deepScrub(event.request.data);
    const headers = event.request.headers;
    if (headers) {
      for (const key of Object.keys(headers)) {
        const lk = key.toLowerCase();
        if (lk === "referer" || lk === "referrer") headers[key] = scrubUrl(headers[key]);
        else if (lk === "cookie" || lk === "authorization" || lk === "sign-secret-id") delete headers[key];
      }
    }
  }
  if (event.user) event.user = event.user.id ? { id: event.user.id } : {};
  // Free-text carriers — the most common PHI vector.
  if (event.message) event.message = redactPii(event.message);
  if (event.exception && event.exception.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = redactPii(ex.value);
      if (ex.type) ex.type = redactPii(ex.type);
    }
  }
  // Structured bags — recurse, drop PHI keys, redact remaining values.
  if (event.extra) event.extra = deepScrub(event.extra);
  if (event.contexts) event.contexts = deepScrub(event.contexts);
  if (event.tags) {
    for (const k of Object.keys(event.tags)) {
      if (typeof event.tags[k] === "string") event.tags[k] = redactPii(event.tags[k]);
    }
  }
  return event;
}

export function scrubBreadcrumb(crumb) {
  if (crumb.category === "console") return null; // may carry PHI in logged objects
  if (crumb.category === "ui.input") return null; // typed input values are high PHI risk
  if (crumb.message) crumb.message = redactPii(crumb.message);
  if (crumb.data) {
    for (const k of Object.keys(crumb.data)) {
      const v = crumb.data[k];
      if (typeof v === "string") {
        crumb.data[k] = k === "url" || k === "from" || k === "to" ? scrubUrl(v) : redactPii(v);
      }
    }
    crumb.data = deepScrub(crumb.data);
  }
  return crumb;
}
